// Interactive walkthrough data for the Field Services MVP.
// Every message body below is the actual template copy we'll ship.

export const ACTORS = {
  resident:   { label: "Resident",      sub: "Jamie · Unit 204",        icon: "User",        tone: "sand" },
  ai:         { label: "AI Concierge",  sub: "the community line",      icon: "Sparkles",    tone: "red" },
  system:     { label: "System",        sub: "router · timers",         icon: "Cog",         tone: "slate" },
  tech:       { label: "On-site crew",  sub: "Marco · prefers Spanish", icon: "HardHat",     tone: "amber" },
  dispatcher: { label: "Dispatcher",    sub: "Rosa · vendor office",    icon: "Headset",     tone: "indigo" },
  manager:    { label: "Manager",       sub: "Dana · Maplewood",        icon: "ClipboardList", tone: "emerald" },
  board:      { label: "Board",         sub: "read-only oversight",     icon: "Landmark",    tone: "slate" },
  support:    { label: "Redline support", sub: "super admin",           icon: "LifeBuoy",    tone: "cyan" },
};

export const ZONES = [
  {
    id: "z1", label: "Zone 1 — Management app", sub: "authenticated dashboard",
    items: ["Work orders — every commitment, its budget line and its paperwork gate",
            "Visit queue · Today & schedules — the recurring loop",
            "Projects — post a job, four rings, compare bids, award",
            "Invoices — check, approve, hand to accounting, mark paid",
            "Vendors — approve for this property, say which trades they own here",
            "Board: the same views, read-only, plus posting projects and signing over the threshold"],
  },
  {
    id: "z2", label: "Zone 2 — Vendor zone", sub: "public + tokenized · NO login, ever",
    items: ["Company onboarding (6 steps, e-signed — insurance optional)",
            "Crew onboarding (per person: OTP + consent + language)",
            "Their own bid page: scope, documents, Q&A, request a site visit, submit a price",
            "Their own work orders: progress, photos, punch list, submit an invoice",
            "Their own alert switchboard: which trades and which towns they hear about",
            "Per-job link: Accept · Assign · En-route · Done + photo"],
  },
  {
    id: "z3", label: "Zone 3 — Resident & the public", sub: "concierge, no control surface",
    items: ["Text or MMS a photo to the community line", "Gets an ack, a named day, then before + after",
            "Rates the fix — feeds the vendor scorecard",
            "The open work board at /projects — no login, community name masked"],
  },
  {
    id: "z4", label: "Zone 4 — Automation", sub: "schedulers + parsers + gates",
    items: ["Escalation timers (+10 office, +20 manager)",
            "Completion watchdog: nudge → nudge → office → manager",
            "Nothing ever auto-closes — silence is never \"done\"",
            "Recurring visit generator + missed-visit watchdog",
            "The paperwork gate, re-run automatically every time a document lands",
            "Insurance expiry 30/14/7/0 + hard block on live work, no override",
            "Bid deadline: 48h nudge to openers, and a rescue if nothing came in",
            "Invoice checks: committed vs billed, change orders, proof of completion",
            "Inbound SMS · MMS · email → AI parse (EN + ES)"],
  },
];

const F = (id, label, sub, tag, steps) => ({ id, label, sub, tag, steps });

export const FLOWS = [
  F("reactive", "Garbage in the hallway", "Track A · reactive · the main loop", "core", [
    {
      t: "9:14a", title: "Resident sends a photo", by: "resident",
      desc: "Jamie MMS's a photo of a trash bag to the community's number. No app, no login, no form.",
      msgs: [
        { to: "ai", ch: "MMS", kind: "in", body: { en: "[photo] there's garbage in the 2nd floor hallway by the elevator" } },
        { to: "resident", ch: "SMS", kind: "out", body: { en: "Got it Jamie — I can see it. I'm sending this straight to the on-site cleaning crew now. I'll text you the moment it's handled. (WO-1042)" } },
      ],
      writes: ["actions.insert → WO-1042  status: REPORTED", "category: common_area_cleaning", "location: Bldg A · Floor 2 · elevator lobby", "before_photos[0] → object storage"],
      note: "If the photo had no location, the AI asks exactly one question (\"which floor?\") and does NOT dispatch. A job with no location is a wasted trip.",
    },
    {
      t: "9:14a", title: "Three gates, before anyone is texted", by: "system",
      desc: "resolve_assignee(community, category, now) runs. One function, one source of truth for routing.",
      gates: [
        { label: "Duplicate? No open WO, same category + location, in the last 12h", ok: true },
        { label: "COI valid — GL $1M · WC · addl-insured · expires 2027-03-15", ok: true },
        { label: "In scope — \"common area garbage\" is in this vendor's scope list", ok: true },
        { label: "Coverage — Tue 9:14a falls inside Mon–Sat 6a–2p → Marco is on", ok: true },
        { label: "Routable — Marco is phone-verified, consented, not opted out", ok: true },
      ],
      writes: ["routed_via: direct", "assigned_contact: Marco (tech)", "timers armed → +10m dispatcher · +20m manager"],
      note: "Fail any gate and it never reaches the crew. Expired COI = hard block. Out of scope = dispatcher decides, never auto-assigned to the tech.",
    },
    {
      t: "9:14a", title: "Straight to the person on site", by: "system",
      desc: "The crew member is texted directly — no hop through the vendor's office. The dispatcher is copied so she has full visibility but no work to do.",
      msgs: [
        { to: "tech", ch: "MMS", kind: "out", body: {
          es: "Maplewood — Piso 2, vestíbulo del elevador. Bolsa de basura en el pasillo. [foto]\nToca para aceptar: rdl.to/j/4K2p\nO responde ACEPTO.",
          en: "Maplewood — Floor 2, elevator lobby. Trash bag in the hallway. [photo]\nTap to accept: rdl.to/j/4K2p\nOr reply ACCEPT." } },
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "FYI — WO-1042 (Floor 2 garbage) went directly to Marco at 9:14a. Nothing needed from you. We'll ping you at 9:24a only if he hasn't accepted. rdl.to/v/9x1" } },
        { to: "manager", ch: "in-app", kind: "out", body: { en: "WO-1042 dispatched · Sparkle Clean · Marco · direct" } },
      ],
      writes: ["status: DISPATCHED", "notified[]: tech(mms), dispatcher(sms cc), manager(in-app)"],
      note: "Marco's text is in Spanish because he picked Spanish during his own onboarding. Rosa's is in English. Same record, two languages.",
    },
    {
      t: "9:15a", title: "Accepted — first tap wins", by: "tech",
      desc: "Marco taps Accept on the link (or just replies \"acepto\" — both work).",
      msgs: [
        { to: "ai", ch: "SMS", kind: "in", body: { es: "acepto, voy en 10 min", en: "accepted, there in 10 min" } },
        { to: "resident", ch: "SMS", kind: "out", body: { en: "Marco from the cleaning crew has it — he's heading up now. I'll send you a photo the second it's clear." } },
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "Marco accepted WO-1042 at 9:15a. Escalation cancelled." } },
      ],
      writes: ["atomic claim: find_one_and_update guarded on status=DISPATCHED → won", "status: SCHEDULED · eta 9:25a", "escalation timers CANCELLED", "timeline: original ES text + EN gloss stored"],
      note: "The claim is atomic, so if Rosa and Marco both hit accept, exactly one wins and the other is told it's already taken.",
    },
    {
      t: "9:41a", title: "Done, with proof", by: "tech",
      desc: "Marco texts a photo of the clean hallway. The AI parses Spanish natively.",
      msgs: [
        { to: "ai", ch: "MMS", kind: "in", body: { es: "listo, ya lo saqué [foto]", en: "done, took it out [photo]" } },
        { to: "resident", ch: "MMS", kind: "out", body: { en: "All clear — here's the before and after. [2 photos]\nAll good? Reply 1 = yes, 2 = still an issue." } },
        { to: "manager", ch: "SMS", kind: "out", body: { en: "WO-1042 completed by Marco at 9:41a — 27 min end to end. Photo proof attached. Resident notified." } },
        { to: "board", ch: "in-app", kind: "out", body: { en: "WO-1042 · completed · proof on file" } },
      ],
      writes: ["status: COMPLETED", "after_photos[0] → object storage", "duration: 27m · within SLA", "scorecard event: response 1m · completion 27m"],
      note: "The resident's own photo becomes the \"before\". Pairing it with the \"after\" costs us nothing and it's the moment the manager shows the board.",
    },
    {
      t: "9:44a", title: "Closed by the resident", by: "resident",
      desc: "Jamie confirms. The loop closes without the manager touching it once.",
      msgs: [
        { to: "ai", ch: "SMS", kind: "in", body: { en: "1 — thank you, that was fast" } },
        { to: "resident", ch: "SMS", kind: "out", body: { en: "Glad it's sorted. How'd the crew do? Reply 1–5." } },
      ],
      writes: ["status: CLOSED", "resident_rating: 5", "manager touches this job: 0"],
      note: "Reply 2 → reopened, re-dispatched, manager alerted. No reply → auto-close after 24h. Either way it never sits in limbo.",
    },
  ]),

  F("stall", "Nobody answers", "Track A · escalation branch", "branch", [
    {
      t: "9:14a", title: "Same dispatch, no response", by: "system",
      desc: "WO-1042 goes to Marco exactly as before. But this time his phone is in his pocket and he's running a floor buffer.",
      writes: ["status: DISPATCHED", "ack deadline: 9:24a", "manager deadline: 9:34a"],
      note: "The resident has already been told someone's on it — so a silent stall here is the one thing that would break trust. This branch exists so it can't happen.",
    },
    {
      t: "9:24a", title: "+10 min → the dispatcher, actionably", by: "system",
      desc: "Now — and only now — the vendor's office is asked to do something.",
      msgs: [
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "Action needed — WO-1042 (Floor 2 garbage, Maplewood) hasn't been accepted. Marco hasn't responded in 10 min.\nReassign or take it: rdl.to/v/9x1\nOr reply with a name: \"send Luis\"" } },
        { to: "tech", ch: "SMS", kind: "out", body: { es: "Recordatorio: WO-1042 sigue esperando. rdl.to/j/4K2p", en: "Reminder: WO-1042 is still waiting. rdl.to/j/4K2p" } },
      ],
      writes: ["escalation_state: dispatcher", "routed_via: direct → dispatcher_escalated"],
      note: "Rosa can reassign by tapping, or by replying with a name. Whoever she names gets the job — and if that person isn't onboarded yet, she's told so immediately.",
    },
    {
      t: "9:34a", title: "+20 min → the manager finds out", by: "system",
      desc: "Both the crew and the office have gone quiet. Dana is told before the resident ever has to ask again.",
      msgs: [
        { to: "manager", ch: "SMS", kind: "out", body: { en: "Nobody has WO-1042 (Floor 2 garbage, reported 9:14a). Marco and Rosa both unresponsive for 20 min. Take it over: rdl.to/wo/1042" } },
        { to: "resident", ch: "SMS", kind: "out", body: { en: "Quick update Jamie — still getting a crew member on this. Dana from management is on it now. Not forgotten." } },
      ],
      writes: ["escalation_state: manager", "flag: vendor_no_response (feeds scorecard)"],
      note: "Three consecutive no-responses from the same crew member flags the roster as stale, and Dana sees \"crew roster last confirmed 34 days ago\" on the vendor's record.",
    },
  ]),

  F("accepted-silence", "Accepted, then silence", "Track A · completion watchdog", "branch", [
    {
      t: "9:15a", title: "Accepted, with an ETA — that becomes a deadline", by: "tech",
      desc: "Marco accepts and says ten minutes. We treat that as a commitment with a clock, not a hope.",
      gates: [
        { label: "He gave a time → deadline = 9:25a + 15 min grace = 9:40a", ok: true },
        { label: "No time given → category SLA (common-area cleaning 4 working hours · hazard 1 hour)", ok: true },
        { label: "Deadline clamped inside his coverage window (6a–2p) — we never expect a 3am close-out", ok: true },
      ],
      writes: ["status: SCHEDULED · eta 9:25a", "completion_deadline: 9:40a", "watchdog armed: nudge · nudge · office · manager"],
      note: "Accepting a job used to be the end of our visibility. It's actually the start of the second, more common failure: the work gets done and nobody closes it out — or it doesn't get done and nobody says so.",
    },
    {
      t: "9:40a", title: "Nudge one — Marco only, nobody else bothered", by: "system",
      msgs: [
        { to: "tech", ch: "SMS", kind: "out", body: {
          es: "¿Ya quedó listo el WO-1042 (piso 2)?\nToca Listo: rdl.to/j/4K2p\nO toca \"Necesito más tiempo\".",
          en: "Is WO-1042 (Floor 2) finished?\nTap Done: rdl.to/j/4K2p\nOr tap \"Need more time\"." } },
      ],
      writes: ["nudge 1 sent · no status change · nobody else notified"],
      note: "Nine times out of ten the work is done and he simply didn't close it out. A soft nudge with a \"need more time\" button clears most of these without involving a single other human. Tapping it buys 30 minutes, once, and it's logged.",
    },
    {
      t: "9:55a", title: "Nudge two — and the resident is told before they wonder", by: "system",
      msgs: [
        { to: "tech", ch: "SMS", kind: "out", body: { es: "Segundo aviso: WO-1042 sigue abierto. rdl.to/j/4K2p", en: "Second reminder: WO-1042 is still open. rdl.to/j/4K2p" } },
        { to: "resident", ch: "SMS", kind: "out", body: { en: "Quick update Jamie — the crew's running a little behind on the hallway. Still on it, and I'll confirm the second it's clear." } },
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "Heads up — Marco accepted WO-1042 at 9:15a and hasn't closed it out. Nudged twice. Nothing needed from you yet." } },
      ],
      writes: ["nudge 2 sent", "resident_reassured_at: 9:55a"],
      note: "\"Running a little behind\" is infinitely better than silence. Silence is exactly what makes a resident call the management office — which is the cost we're trying to remove.",
    },
    {
      t: "10:10a", title: "+55 min → the office owns it", by: "system",
      msgs: [
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "Action needed — WO-1042 was accepted by Marco 55 min ago and still isn't closed. Confirm it's done, or reassign: rdl.to/v/9x1" } },
      ],
      gates: [{ label: "Status → OVERDUE (accepted but not completed)", ok: false }],
      writes: ["status: OVERDUE", "escalation_state: dispatcher", "flag: accepted_not_completed"],
      note: "This is a DIFFERENT red than \"nobody answered\". The manager's board separates No response from Accepted-and-overdue, because they're two completely different conversations to have with a vendor.",
    },
    {
      t: "10:25a", title: "Manager can take it back", by: "system",
      msgs: [
        { to: "manager", ch: "SMS", kind: "out", body: { en: "WO-1042 (Floor 2 garbage) — Marco accepted at 9:15a, still not closed 70 min in. Take it back and re-dispatch: rdl.to/wo/1042" } },
      ],
      writes: ["escalation_state: manager", "action available: Reclaim → status back to REPORTED → re-dispatch to backup"],
      note: "Nothing auto-closes. Ever. Silence is never treated as done — only Marco tapping Done or Jamie replying 1 closes a job. That single rule is what makes the completion stats we're selling actually true.",
    },
    {
      t: "10:31a", title: "He finishes — but sends no photo", by: "tech",
      msgs: [
        { to: "ai", ch: "SMS", kind: "in", body: { es: "listo", en: "done" } },
        { to: "tech", ch: "SMS", kind: "out", body: { es: "Gracias. Manda una foto del pasillo para cerrarlo por completo.", en: "Thanks. Send a photo of the hallway to fully close it out." } },
        { to: "resident", ch: "SMS", kind: "out", body: { en: "The hallway's been cleared. I'll follow up with a photo shortly." } },
        { to: "manager", ch: "in-app", kind: "out", body: { en: "WO-1042 completed 10:31a — NO PHOTO on file (photos are required for this category). Late by 51 min." } },
      ],
      writes: ["status: COMPLETED_UNVERIFIED", "proof_missing: true", "scorecard: late_close +1 · accepted_not_completed +1"],
      note: "We don't hold the resident's answer hostage to a photo. But the manager can see exactly which jobs closed without proof, and \"accepts fast, closes late\" becomes a number on the vendor's scorecard instead of a feeling.",
    },
  ]),

  F("recurring", "Tue/Thu clean + Mon–Sat garbage", "Track D · recurring · proof of service", "core", [
    {
      t: "5:45a", title: "The scheduler builds today", by: "system",
      desc: "Two separate schedules on one vendor at one community. Today is Tuesday, so both fire.",
      gates: [
        { label: "Garbage check — Mon–Sat, 6:00–8:00a, photo optional", ok: true },
        { label: "Full property clean — Tue + Thu, 6:00–10:00a, 9-item checklist, photos required", ok: true },
        { label: "COI still valid → visits allowed to generate", ok: true },
      ],
      writes: ["fs_visits.insert → V-3391 (garbage check)", "fs_visits.insert → V-3392 (full clean)", "assigned: Marco (schedule default)"],
      note: "This is why schedules are plural per vendor. One cleaning company, two cadences, two checklists, two windows — modelled as data, not code.",
    },
    {
      t: "5:50a", title: "Today's work, in his language", by: "system",
      msgs: [
        { to: "tech", ch: "SMS", kind: "out", body: {
          es: "Buenos días Marco. Hoy en Maplewood:\n1) Revisión de basura (6–8a)\n2) Limpieza completa (6–10a) — 9 puntos, se requieren fotos\nAbre tu lista: rdl.to/v/3391",
          en: "Morning Marco. Today at Maplewood:\n1) Garbage check (6–8a)\n2) Full clean (6–10a) — 9 items, photos required\nOpen your checklist: rdl.to/v/3391" } },
      ],
      writes: ["visit status: SCHEDULED"],
      note: "One text, both visits, tappable checklist. Works on a $60 Android with no login.",
    },
    {
      t: "6:58a", title: "Check-in = proof he was there", by: "tech",
      desc: "Marco taps Check in (or replies AQUI / HERE).",
      msgs: [{ to: "ai", ch: "SMS", kind: "in", body: { es: "aqui", en: "here" } }],
      writes: ["V-3391 status: CHECKED_IN @ 6:58a", "V-3392 status: CHECKED_IN"],
      note: "Later, this check-in is what lets us auto-detect who's on site today without anyone maintaining a roster. That's the fast-follow, not the MVP.",
    },
    {
      t: "7:40a", title: "Checklist + photos → the manager's morning", by: "tech",
      msgs: [
        { to: "ai", ch: "MMS", kind: "in", body: { es: "9 de 9 listo [4 fotos]", en: "9 of 9 done [4 photos]" } },
        { to: "manager", ch: "email", kind: "out", body: { en: "Maplewood — this morning: garbage check ✓ 6:58a · full clean ✓ 9/9 items, 7:40a. 4 photos on file. Nothing missed." } },
      ],
      writes: ["V-3391 · V-3392 status: DONE", "checklist_state: 9/9", "photos[] → object storage", "adherence: on-time"],
      note: "That email is the whole pitch to a board: verified, photographed, timestamped service — without the manager standing there watching.",
    },
    {
      t: "8:15a", title: "Missed-visit watchdog (the other branch)", by: "system",
      desc: "If nobody had checked in by the end of the window:",
      msgs: [
        { to: "manager", ch: "SMS", kind: "out", body: { en: "Missed visit — Maplewood garbage check (Mon–Sat 6–8a) had no check-in today. Sparkle Clean nudged. rdl.to/sch" } },
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "No check-in for today's 6–8a garbage check at Maplewood. Who's covering?" } },
      ],
      writes: ["visit status: MISSED", "scorecard: missed_visit +1"],
      note: "Catching a missed visit is the single stat that pays for the module — nobody currently knows when a contracted service quietly didn't happen.",
    },
  ]),

  F("vendor-onboard", "Onboarding the company", "one time · e-signed", "setup", [
    {
      t: "step 1", title: "Manager invites them", by: "manager",
      desc: "Dana enters the company name and the office contact's mobile + email. That's all she types.",
      writes: ["vendors.insert → status: invited", "vendor_account_links → pending"],
    },
    {
      t: "step 2", title: "The office contact gets a link", by: "system",
      msgs: [{ to: "dispatcher", ch: "SMS", kind: "out", body: { en: "Maplewood HOA (Dana) has set you up on Ask Redline so cleaning requests reach your crew directly instead of by phone tag. 6 quick steps, no account to create: rdl.to/o/7Qm" } }],
      note: "White-labeled as the management company — the vendor experiences this as their client's system, not a third-party tool.",
    },
    {
      t: "step 3", title: "Six steps, no login", by: "dispatcher",
      desc: "The wizard, in order:",
      gates: [
        { label: "1 · How this works — what texts arrive and what's expected of them", ok: true },
        { label: "2 · Business identity — legal name, DBA, EIN, full address", ok: true },
        { label: "3 · Scope — trades, towns served, default language, what they will and won't take", ok: true },
        { label: "4 · Contacts & crew — office, field crew, billing (each gets their own invite)", ok: true },
        { label: "5 · Certificate of insurance and W-9 — OPTIONAL here, added later from the same link", ok: true },
        { label: "6 · E-sign the vendor agreement — the only thing Gate 1 actually requires", ok: true },
      ],
      writes: ["vendor_contacts.insert × N → status: invited", "agreement: signed (reuses the signature engine)", "status → pending_approval"],
      note: "Insurance used to be required to finish this wizard, and that's where most companies quietly stopped. Now they can finish, be found, hear about work and come out to look — they just can't bid or be handed a job until the documents land. If they stop half-way, their office is reminded on day 1, 3, 7 and 14 with exactly what's outstanding and a link straight back to where they left off. Then we stop asking and tell the manager.",
    },
    {
      t: "step 4", title: "Manager approves what she can see", by: "manager",
      desc: "Five steps: who they are · read the certificate · the paperwork · what they own here · approve.",
      gates: [
        { label: "General liability $1,000,000 — meets this community's minimum", ok: true },
        { label: "Named as additional insured", ok: true },
        { label: "Agreement signed 9:02a by R. Alvarez", ok: true },
        { label: "W-9 still outstanding — shown, but doesn't block approval", ok: false },
      ],
      writes: ["vendor_community_links → active", "in_scope_categories: [common_area_cleaning, trash]"],
      note: "Insurance is shown here and never required. Approving a company with a gap is fine — they'll read as \"can be told about work, can't take a job\" until it's fixed, and we chase them for it automatically.",
    },
    {
      t: "step 5", title: "Assign to the community", by: "manager",
      desc: "The only real configuration — and every field has a sane default she can leave alone.",
      gates: [
        { label: "Trades here: common-area cleaning · trash", ok: true },
        { label: "In scope: hallway garbage, litter, spills, trash room", ok: true },
        { label: "Out of scope: biohazard, carpet extraction → dispatcher decides, never auto-sent", ok: true },
        { label: "Routing: crew first → office at +10 min → me at +20 min", ok: true },
        { label: "Coverage: Mon–Sat 6a–2p (Marco) · after hours → office", ok: true },
        { label: "Schedules: garbage Mon–Sat 6–8a · full clean Tue/Thu 6–10a", ok: true },
      ],
      writes: ["vendor_community_links.insert", "fs_site_assignments.insert", "fs_schedules.insert × 2"],
    },
  ]),

  F("crew-onboard", "Onboarding each crew member", "per person · ~60 seconds", "setup", [
    {
      t: "step 1", title: "Someone adds a person", by: "dispatcher",
      desc: "Rosa adds Marco from her tokenized portal — during setup, or any Tuesday six months from now when she hires someone. Dana or Redline support can also add him for her (see the \"Support steps in\" flow).",
      gates: [
        { label: "Vendor's own office — the normal path", ok: true },
        { label: "Community manager — sitting with the vendor at kickoff", ok: true },
        { label: "Redline support / super admin — when the vendor is stuck", ok: true },
        { label: "Whoever did it is stamped on the record, and both sides are told", ok: true },
      ],
      writes: ["vendor_contacts.insert → Marco · role: tech · status: invited · NOT routable", "added_by: {user, role, on_behalf_of: Sparkle Clean}"],
      note: "This is the answer to \"how would you know who the on-site person is\": the company tells us, once, and can change it any time in one tap. We never guess.",
    },
    {
      t: "step 2", title: "He gets his own invite — bilingual", by: "system",
      msgs: [{ to: "tech", ch: "SMS", kind: "out", body: { en: "Sparkle Clean added you for Maplewood. Requests will come straight to this phone.\nReply 1 for English · Responde 2 para Español" } }],
      note: "We don't assume his language from his employer. He picks. Everything after this point is in the language he chose.",
    },
    {
      t: "step 3", title: "He verifies his own phone", by: "tech",
      desc: "Six-digit code, rate-limited — the same verification the platform already uses for residents.",
      msgs: [
        { to: "ai", ch: "SMS", kind: "in", body: { es: "2", en: "2" } },
        { to: "tech", ch: "SMS", kind: "out", body: { es: "Tu código es 418-233. Ábrelo aquí: rdl.to/c/8Wp", en: "Your code is 418-233. Open here: rdl.to/c/8Wp" } },
      ],
      writes: ["verify_status: verified", "preferred_language: es"],
    },
    {
      t: "step 4", title: "Consent, in his language, logged", by: "tech",
      desc: "He explicitly agrees to receive work texts, and is told how to stop them.",
      gates: [
        { label: "Acepto recibir mensajes de trabajo en este número", ok: true },
        { label: "Responde STOP en cualquier momento · HELP para ayuda", ok: true },
        { label: "Mi número nunca se comparte con los residentes", ok: true },
      ],
      writes: ["consent: {granted_at, phone, ip, language: es, notice_version: v1}"],
      note: "He does NOT sign the vendor agreement — his employer did. He gives a logged acknowledgment. Different documents, on purpose: it keeps the company as the contracted party and keeps the HOA out of any \"who employs this person\" argument.",
    },
    {
      t: "step 5", title: "Three cards, then he's done", by: "tech",
      gates: [
        { label: "Recibirás un mensaje con una foto y la ubicación", ok: true },
        { label: "Toca Aceptar — o responde ACEPTO", ok: true },
        { label: "Al terminar, toca Listo y manda una foto", ok: true },
      ],
      writes: ["status: verified → ROUTABLE", "receives[]: new_job, onsite, completion"],
      note: "Until this finishes he is not routable, and the router quietly falls back to the dispatcher while Dana sees \"1 crew member pending verification\". A job never disappears into an unverified phone.",
    },
    {
      t: "any time", title: "He can opt out without breaking anything", by: "tech",
      desc: "If Marco ever texts STOP:",
      msgs: [
        { to: "manager", ch: "in-app", kind: "out", body: { en: "Marco (Sparkle Clean) opted out of texts. Jobs for Maplewood now route to the office first. Consider asking Rosa for a replacement contact." } },
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "Marco opted out of job texts. His jobs will come to you until you add another crew member." } },
      ],
      writes: ["status: stopped · NOT routable", "routing falls back: dispatcher first"],
      note: "Honoring STOP is not optional, and dropping his jobs silently would be worse than not texting him at all. So opt-out degrades the routing instead of breaking it.",
    },
  ]),

  F("coi", "Insurance lapses", "compliance · hard block", "branch", [
    {
      t: "T-30 days", title: "First reminder, to the office", by: "system",
      msgs: [{ to: "dispatcher", ch: "SMS", kind: "out", body: { en: "Heads up — Sparkle Clean's certificate of insurance for Maplewood expires in 30 days (Mar 15). Upload the new one, no login needed: rdl.to/coi/2Fh" } }],
      writes: ["coi_reminder: 30d sent"],
    },
    {
      t: "T-15 days", title: "Second reminder — manager copied", by: "system",
      msgs: [
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "Reminder: insurance expires in 15 days. After that we can't send your crew any jobs. rdl.to/coi/2Fh" } },
        { to: "manager", ch: "email", kind: "out", body: { en: "Sparkle Clean's COI expires Mar 15 (15 days). They've been reminded twice. If it lapses, dispatch to them stops automatically." } },
      ],
      writes: ["coi_reminder: 15d sent"],
    },
    {
      t: "expiry day", title: "Hard block", by: "system",
      desc: "This is the part that makes a manager exhale.",
      gates: [{ label: "COI expired → vendor NOT deployable", ok: false }],
      msgs: [{ to: "manager", ch: "SMS", kind: "out", body: { en: "Sparkle Clean's insurance expired today. New work orders to them are BLOCKED and today's recurring visits are paused. Nothing will be dispatched to an uninsured vendor. Assign a backup or chase the COI: rdl.to/v/9x1" } }],
      writes: ["vendors.status → coi_expired", "dispatch: BLOCKED at the gate", "recurring visits: PAUSED"],
      note: "Not a warning banner someone ignores. The router refuses. That's the difference between a compliance report and a compliance system.",
    },
    {
      t: "next day", title: "New certificate → back on automatically", by: "dispatcher",
      msgs: [{ to: "manager", ch: "in-app", kind: "out", body: { en: "Sparkle Clean uploaded a new COI — GL $1M ✓ WC ✓ addl-insured ✓ expires 2028-03-15. Verified and reactivated. Recurring visits resume tomorrow 6a." } }],
      writes: ["coi: re-parsed · Verified", "vendors.status → active", "visits: RESUMED"],
    },
  ]),

  F("solo-vendor", "A one-person vendor", "no office · no dispatcher", "core", [
    {
      t: "onboarding", title: "The wizard asks instead of assuming", by: "dispatcher",
      desc: "Luis runs a two-truck cleaning outfit. He answers the phone, he mops the floor, he sends the invoice. There is no office.",
      gates: [
        { label: "\"Is there an office or dispatcher separate from the people doing the work?\" → No", ok: true },
        { label: "Luis is stored as ONE contact holding BOTH roles: dispatcher + tech", ok: true },
        { label: "No crew to add — he is the crew. Zero extra setup.", ok: true },
      ],
      writes: ["vendor_contacts: 1 record · roles: [dispatcher, tech]", "dispatch_mode: direct_then_dispatcher (chain collapses at runtime)"],
      note: "You're right that the design assumed every vendor has a dispatcher. Most small vendors are one to three people. So the dispatcher tier is now OPTIONAL — one question on one screen removes the biggest wrong assumption in the design.",
    },
    {
      t: "9:14a", title: "The escalation chain collapses itself", by: "system",
      gates: [
        { label: "Rung 1 · on-site tech → Luis  (+1 512 555 0134)", ok: true },
        { label: "Rung 2 · dispatcher → Luis — same phone, SKIPPED", ok: false },
        { label: "Rung 3 · manager → Dana", ok: true },
      ],
      writes: ["chain: [Luis t0] → [Luis reminder +10m] → [Dana +20m]", "deduped by phone number"],
      note: "The rule is one line and it kills a whole class of bugs: never send the same person the same escalation twice. If the next rung resolves to a phone we already pinged, we skip to the rung after it — and the reminder we do send is worded differently so it doesn't read like a copy-paste.",
    },
    {
      t: "9:24a", title: "Same phone, sharper words", by: "system",
      msgs: [
        { to: "tech", ch: "SMS", kind: "out", body: { en: "Luis — WO-1042 (Maplewood, Floor 2 garbage) is still unaccepted 10 min in. You're the only contact on this account, so if you can't take it, reply CAN'T and Dana will know right away." } },
      ],
      writes: ["escalation_state: solo_reminder"],
      note: "Giving him an explicit CAN'T is worth more than another nudge. A fast no beats a slow maybe for the manager.",
    },
    {
      t: "9:34a", title: "The manager's message never mentions a dispatcher", by: "system",
      msgs: [
        { to: "manager", ch: "SMS", kind: "out", body: { en: "Nobody has WO-1042 (Floor 2 garbage). Luis at Sparkle Clean is a one-person vendor and hasn't responded in 20 min. Take it over: rdl.to/wo/1042" } },
      ],
      writes: ["escalation_state: manager"],
      note: "The copy never says \"the dispatcher\" — because there isn't one. Every manager-facing message is generated from who actually exists on the account, not from a template that assumes an office. Getting this wrong is how software feels stupid.",
    },
  ]),

  F("multi-property", "One vendor, three buildings", "multi-property routing", "core", [
    {
      t: "setup", title: "One company, one identity, one certificate", by: "manager",
      desc: "Sparkle Clean already cleans Maplewood. Oakridge's manager invites them too — and we recognise them instead of creating a second company.",
      gates: [
        { label: "Matched by the office phone number → existing company found", ok: true },
        { label: "Insurance already verified → carries over, no re-upload", ok: true },
        { label: "Agreement already signed → carries over, no re-signing", ok: true },
        { label: "Crew list carries over → Marco does NOT re-onboard", ok: true },
        { label: "A second, duplicate company record", ok: false },
      ],
      writes: ["vendor_community_links.insert → Oakridge (status: pending_approval)", "vendors: unchanged — one identity", "vendor_contacts: unchanged — one crew list"],
      note: "This is the bug I'd have shipped: inviting the same cleaner at a second property would have created a second company with its own certificate and its own crew list, and Marco would have been asked to verify his phone all over again. One company, many properties.",
    },
    {
      t: "setup", title: "Different person at each building", by: "dispatcher",
      desc: "Rosa opens the same link she's always had. It now shows every property, and she names who's on site at each.",
      gates: [
        { label: "Maplewood → Marco (primary) · Luis (backup) · Mon–Sat 6a–2p", ok: true },
        { label: "Oakridge → Luis (primary) · nobody backup · Tue/Thu 7a–11a", ok: true },
        { label: "Riverbend → nobody named yet → jobs there go to Rosa's office", ok: true },
      ],
      writes: ["fs_site_assignments: one row PER PROPERTY", "coverage windows: per property, per person"],
      note: "The vendor sets this, not the manager — Rosa knows who's where. And a property with nobody named doesn't break: it just routes to the office instead.",
    },
    {
      t: "9:14a", title: "Maplewood reports something → only Marco hears about it", by: "system",
      msgs: [
        { to: "tech", ch: "MMS", kind: "out", body: {
          es: "Maplewood — Piso 2, vestíbulo del elevador. Basura en el pasillo. [foto]\nToca para aceptar: rdl.to/j/4K2p",
          en: "Maplewood — Floor 2, elevator lobby. Garbage in the hallway. [photo]\nTap to accept: rdl.to/j/4K2p" } },
      ],
      writes: ["resolved at Maplewood only → Marco", "Luis at Oakridge: not notified", "Rosa: CC'd (she covers every property)"],
      note: "The property name leads every message, because a crew member working three buildings needs to know which one before anything else. Luis never sees Maplewood's work.",
    },
    {
      t: "9:20a", title: "Oakridge reports something six minutes later", by: "system",
      msgs: [
        { to: "tech", ch: "MMS", kind: "out", body: {
          en: "Oakridge — Pool gate, broken glass on the deck. [photo]\nTap to accept: rdl.to/j/7Bv2" } },
      ],
      writes: ["resolved at Oakridge only → Luis", "Oakridge coverage Tue/Thu 7–11a: 9:20a Tue ✓ inside", "two open jobs, two properties, two people, one company"],
      note: "Same company, same minute, two completely separate loops. Each property's manager only ever sees their own property's jobs — even though it's one vendor.",
    },
    {
      t: "11:05a", title: "Riverbend reports something outside anyone's hours", by: "system",
      msgs: [
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "Riverbend — trash room overflowing. Nobody is named on site there yet, so this one's yours: rdl.to/v/9x1\nWant it to go straight to someone? Name them here: rdl.to/v/9x1" } },
      ],
      writes: ["routed_via: dispatcher_fallback", "flag: no site assignment at Riverbend"],
      note: "Notice it doesn't just fall back silently — it tells Rosa exactly why, and offers the one-tap fix. That's how the roster gets filled in without anyone chasing her.",
    },
    {
      t: "any time", title: "One crew member, several properties", by: "tech",
      desc: "Luis is primary at Oakridge and backup at Maplewood. When he texts or calls, we already know which job he means.",
      msgs: [
        { to: "ai", ch: "SMS", kind: "in", body: { en: "done" } },
        { to: "tech", ch: "SMS", kind: "out", body: { en: "Which one — Oakridge pool gate, or Maplewood floor 2? Reply 1 or 2." } },
      ],
      writes: ["inbound resolved by the community number he texted", "if still ambiguous → we ask, we never guess"],
      note: "Each community has its own number, so texting usually resolves the property by itself. When it genuinely can't, we ask one short question rather than closing the wrong job.",
    },
  ]),

  F("support", "Support steps in", "super admin can add crew", "setup", [
    {
      t: "any time", title: "Three people can add a crew member", by: "support",
      desc: "Rosa's phone died. Or she can't find the link. Or she's just not going to do it. Somebody else has to be able to.",
      gates: [
        { label: "Super admin (Redline) — invite vendors, add/remove crew, resend invites, re-trigger the code", ok: true },
        { label: "Portfolio admin + community manager — same powers, scoped to their communities", ok: true },
        { label: "The vendor's own office — the normal path", ok: true },
        { label: "Every action stamped with who did it, and on whose behalf", ok: true },
      ],
      writes: ["vendor_contacts.insert → added_by: {user: Sam Ortiz, role: super_admin, on_behalf_of: Sparkle Clean}"],
      note: "You asked for this and it's the right call — in a beta, the fastest way to lose a vendor is to leave them stuck on step 4 of a wizard with nobody able to help them.",
    },
    {
      t: "instantly", title: "Both sides are told, immediately", by: "system",
      msgs: [
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "Ask Redline support added Marco Reyes as a field tech on your Sparkle Clean account, at Dana's request. If that's wrong, remove him here: rdl.to/v/9x1" } },
        { to: "manager", ch: "in-app", kind: "out", body: { en: "Marco Reyes added to Sparkle Clean by Ask Redline support (Sam Ortiz) · 10:04a" } },
      ],
      writes: ["timeline: contact_added_by_support", "vendor + manager both notified"],
      note: "Support intervention with no notification is how trust dies. Every helping hand is visible to both sides the moment it happens — nobody discovers a mystery crew member three weeks later.",
    },
    {
      t: "guardrails", title: "Support gets reach, not invisibility", by: "system",
      gates: [
        { label: "Accept or complete a job as the vendor — allowed, but stamped \"set by Ask Redline support\" on the timeline", ok: true },
        { label: "Read-only view of the vendor's job link, for debugging", ok: true },
        { label: "Change any state with no audit trail", ok: false },
        { label: "See another portfolio's private vendor notes or scorecard", ok: false },
        { label: "Bypass the COI hard block", ok: false },
      ],
      writes: ["forced_by_support: {user, reason} on any manual state change"],
      note: "If support can silently mark things complete, the completion stats we're selling become fiction. So the power exists, and it always leaves a fingerprint.",
    },
    {
      t: "the rescue", title: "The half-finished onboarding — the failure that actually happens", by: "support",
      desc: "Marco never finished verifying, so he isn't routable, and every job has been quietly falling through to the office for two weeks.",
      msgs: [
        { to: "tech", ch: "SMS", kind: "out", body: { es: "Sparkle Clean · Maplewood — falta un paso para recibir trabajos en este teléfono: rdl.to/c/8Wp", en: "Sparkle Clean · Maplewood — one step left to get jobs on this phone: rdl.to/c/8Wp" } },
      ],
      writes: ["invite resent (attempt 2) · by: support", "manager sees: \"invite resent by support · 2m ago\""],
      note: "So the vendor's page shows \"1 crew member pending verification\" as a warning with a one-tap resend — instead of routing silently degrading for a month while everyone assumes it's working.",
    },
  ]),

  F("voice", "Calling in instead of texting", "designed · not built", "proposed", [
    {
      t: "10:12a", title: "He calls, because typing is a chore", by: "tech",
      desc: "Gloves on, hands wet, cracked screen, second language. He calls the same community number he's already texting with.",
      gates: [
        { label: "Caller ID matches a verified vendor contact → greeted as CREW, not as a resident", ok: true },
        { label: "His language is already on his record — the call opens in Spanish", ok: true },
        { label: "Realtime voice concierge, caller identity and the action layer already exist", ok: true },
        { label: "This is a new caller TYPE plus about 5 intents — not a new system", ok: true },
      ],
      writes: ["call session: identity = vendor_contact(Marco) · lang: es"],
      note: "You said it yourself — it's a lot to text sometimes. For a janitorial crew this is probably the biggest adoption unlock in the whole module, and it's only cheap because the voice stack is already built.",
    },
    {
      t: "10:12a", title: "It opens with his actual job", by: "ai",
      msgs: [
        { to: "tech", ch: "voice", kind: "out", body: {
          es: "Hola Marco. Tienes un trabajo abierto en Maplewood: basura en el pasillo del piso 2, aceptado a las 9:15. ¿Ya está listo?",
          en: "Hi Marco. You have one open job at Maplewood: garbage in the 2nd floor hallway, accepted at 9:15. Is it done?" } },
      ],
      writes: ["intents: list my jobs · accept · done · need more time · can't do it"],
      note: "No phone menu, no job numbers read aloud. It opens with his open job, because there's only ever one or two. If there were three, it asks which — nothing else.",
    },
    {
      t: "10:13a", title: "He closes one job and opens another, by talking", by: "tech",
      msgs: [
        { to: "ai", ch: "voice", kind: "in", body: {
          es: "sí, ya lo saqué. y hay una mancha en la alfombra del piso 3",
          en: "yes, took it out. and there's a stain on the 3rd floor carpet" } },
      ],
      writes: ["WO-1042 → COMPLETED_UNVERIFIED", "WO-1043 opened from a CREW report: floor 3 carpet stain", "scope check: carpet extraction is OUT of Sparkle Clean's scope → routed to Dana"],
      note: "This is the part I'd argue hardest for. A crew member noticing a second problem while closing the first currently dies in his head. And because carpet extraction isn't in his scope, it goes to Dana with a cost decision — not back to him as unpaid work.",
    },
    {
      t: "10:14a", title: "Voice can't carry a photo — so it hands off to text", by: "system",
      msgs: [
        { to: "tech", ch: "SMS", kind: "out", body: { es: "Gracias Marco. Manda una foto del pasillo para cerrarlo. rdl.to/j/4K2p", en: "Thanks Marco. Send a photo of the hallway to close it out. rdl.to/j/4K2p" } },
        { to: "resident", ch: "SMS", kind: "out", body: { en: "The hallway's clear — photo coming shortly." } },
      ],
      writes: ["status: COMPLETED_UNVERIFIED → COMPLETED once the photo lands"],
      note: "The honest limitation, and why voice supplements texting instead of replacing it. Outbound robocalls to crew stay permanently out — nobody has ever been glad to get one.",
    },
  ]),

  F("spine", "Where every commitment lives", "the work order · read this one first", "core", [
    {
      t: "the idea", title: "One record, or four different stories", by: "system",
      desc: "A promise to a company used to be able to live in four places and be visible in none of them. Now there is one answer to: what have we committed to, to whom, on which budget line, and are they allowed on the property today?",
      gates: [
        { label: "A resident's request gets sent to a vendor → a work order", ok: true },
        { label: "A recurring service is set up → a work order the visits hang off", ok: true },
        { label: "A project is awarded and kicked off → a work order", ok: true },
        { label: "A manager raises a job by hand → a work order", ok: true },
        { label: "There is no fifth way. Nothing reaches a vendor off the spine", ok: true },
      ],
      writes: ["work_orders.insert → WO-1042", "kind: one_off | recurring | project", "status: draft → pending_paperwork → active → in_progress → complete"],
      note: "Every question a board asks six months later — who authorised this, why did we pay more than the bid, was this the company with the lapsed insurance, which budget did it come out of — is answered by reading one record.",
    },
    {
      t: "9:02a", title: "Dana raises a job from a phone call", by: "manager",
      desc: "Someone rings the office about a bin. Five questions, one at a time: what and where · what kind of work · how urgent · who reported it · review.",
      gates: [
        { label: "Normal → goes on the crew's next scheduled visit. Nobody is pulled out", ok: true },
        { label: "Urgent → someone comes out today, skips the visit queue, starts the escalation clock", ok: true },
        { label: "A reporter's mobile means they get told when it's done — and stop ringing the office", ok: true },
      ],
      writes: ["work_orders.insert → kind: one_off · source: manager", "gl_code stamped from the trade default"],
      note: "Urgency isn't a label here, it changes what happens next — so it gets its own question with the consequence written out, instead of being a pill nobody reads.",
    },
    {
      t: "9:02a", title: "It gets a budget line before it gets a vendor", by: "system",
      desc: "The code comes down a chain, so nobody has to remember it: the management company's chart of accounts → this community's overrides → the default for this trade → stamped on the work order.",
      gates: [
        { label: "Editable until the first invoice is approved, then frozen", ok: true },
        { label: "Every invoice against this job inherits it", ok: true },
        { label: "The vendor never sees the code, and never sees the management fee", ok: true },
      ],
      writes: ["gl_code: 5115 · Trash & Refuse Removal", "fee: computed at kickoff, disclosed to the board"],
      note: "Kickoff cannot be completed without a code. That single rule is what stopped invoices arriving with no home.",
    },
    {
      t: "9:02a", title: "The gate: cleared to start, or waiting", by: "system",
      desc: "The work order exists and is coded. The vendor still hasn't been told anything.",
      gates: [
        { label: "Current certificate of insurance meeting this community's minimums", ok: true },
        { label: "W-9 on file", ok: true },
        { label: "Signed vendor agreement", ok: true },
        { label: "Contract signed, if the amount needs one", ok: false },
      ],
      writes: ["status: pending_paperwork", "vendor NOT notified"],
      note: "The gate re-runs itself every time paperwork lands, so nobody has to remember to unblock a job. And a certificate that lapses mid-job pauses live work — there is no emergency override, deliberately.",
    },
  ]),

  F("project-bid", "Putting the roof out for bids", "Track B · four rings · site visits", "core", [
    {
      t: "step 1", title: "Dana posts the project", by: "manager",
      desc: "Seven questions: what & where · scope · budget · who bids · the open market · deadline · review. She can write the scope, dictate it, let the AI draft it from the title, or hand us the document she already has and we read it.",
      gates: [
        { label: "One company — sole source, always with a written reason a board will ask for", ok: true },
        { label: "Companies she ticks off her own list", ok: true },
        { label: "The whole network, matched by trade then area", ok: true },
        { label: "The open market — listed publicly, community name masked", ok: true },
      ],
      writes: ["fs_projects.insert → status: open", "scope revision 1 · 2 documents attached", "budget: $18,000 — never shown to a vendor"],
      note: "The rings combine. Before it goes out she's told exactly how many companies this reaches and who the first three are — no posting into the dark.",
    },
    {
      t: "step 1", title: "Insurance is deliberately NOT a filter here", by: "system",
      desc: "The audience filter has an \"insurance current\" tick box and it's off by default.",
      gates: [
        { label: "Telling a company about work needs no paperwork", ok: true },
        { label: "Answering their question needs no paperwork", ok: true },
        { label: "Walking the property to price the job needs no paperwork", ok: true },
        { label: "Submitting a bid does", ok: false },
      ],
      writes: ["audience: trades ∩ area · 11 companies reachable"],
      note: "This is the change that stopped the funnel leaking. Filtering uninsured companies out of the invite list is how the pool of vendors who ever hear about your work quietly shrinks to the same four names.",
    },
    {
      t: "step 2", title: "Everyone hears about it at once", by: "system",
      msgs: [
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "New project out for bids — Maplewood: reroof the clubhouse (~2,400 sq ft, tear-off). Scope + drawings, ask questions, request a site visit, submit your price: rdl.to/b/7Km\nBids due 24 Jan." } },
        { to: "manager", ch: "in-app", kind: "out", body: { en: "Sent to 11 companies · also listed on the open board" } },
      ],
      writes: ["notified_vendor_ids × 11 · throttled", "public_token minted → /projects/7Km"],
      note: "Throttled, so a big blast can't melt the line. Everyone who opens it and hasn't bid gets exactly one reminder 48 hours before the deadline — and if no deadline was set, one nudge chased off the day they looked instead. The date being optional never switches the chasing off.",
    },
    {
      t: "step 3", title: "A company nobody has heard of finds it", by: "dispatcher",
      desc: "Ring four. The open board at /projects needs no login. Your community's name is masked until they tell us who they are.",
      gates: [
        { label: "Registering takes about thirty seconds", ok: true },
        { label: "Registering fast-tracks them onto this project's invite list", ok: true },
        { label: "They still can't bid until the paperwork is in", ok: true },
      ],
      writes: ["vendors.insert → source: open_board", "project.registered_interest += 1"],
      note: "This is how the network grows without anybody doing outreach. The job advertises itself, and the company that shows up has already read the scope.",
    },
    {
      t: "step 4", title: "The site visit — no paperwork at all", by: "tech",
      desc: "Three companies ask to come and look. Dana picks windows and the access details are released at that point, not before.",
      msgs: [
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "Site visit confirmed — Maplewood clubhouse, Thu 18 Jan, 9–11am. Gate code 4417, park in the visitor bay by the pool. Questions on the day: (512) 555-0140." } },
      ],
      writes: ["site_visits.insert → confirmed", "access details released to this vendor only"],
      note: "Looking at a job is how a company decides whether to price it. Requiring a certificate to walk a car park is how you end up with one bid.",
    },
    {
      t: "step 5", title: "The bid gate — here the paperwork bites", by: "system",
      gates: [
        { label: "Current certificate of insurance", ok: true },
        { label: "W-9", ok: false },
        { label: "Signed vendor agreement", ok: true },
      ],
      msgs: [
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "You're one document away from bidding on the Maplewood roof — we still need your W-9. Upload it right on the bid page, takes a minute: rdl.to/b/7Km" } },
      ],
      writes: ["bid submission BLOCKED · reason: w9", "vendor told exactly what's missing, with an upload button"],
      note: "It names the missing document and lets them fix it inline. \"Contact your account manager\" is how a bid you wanted turns into a bid you never got.",
    },
    {
      t: "step 6", title: "Four bids, side by side", by: "manager",
      desc: "Price, line items, start date, duration, attachments, the company's rating and job count — and a plain-English flag on anyone whose paperwork would hold up a start.",
      gates: [
        { label: "No vendor sees another vendor's price. Ever", ok: true },
        { label: "No vendor sees the budget", ok: true },
        { label: "A question answered once is broadcast to every bidder", ok: true },
      ],
      writes: ["proposals × 4 · low $16,400 · high $23,900"],
      note: "If the deadline passes with nothing in it — or the project just sits there for a fortnight, deadline or not — Dana gets one alert offering three ways out: extend it, widen the audience, or go direct. Then it waits in amber on her Work orders tab until she decides. Nobody needs a weekly reminder that they haven't made a decision.",
    },
  ]),

  F("award-kickoff", "Award, paper, cleared to start", "Track B · where a promise becomes a commitment", "core", [
    {
      t: "step 1", title: "The award, and the board's signature", by: "manager",
      desc: "$16,400 is over Maplewood's $10,000 threshold, so this isn't Dana's call alone.",
      msgs: [
        { to: "board", ch: "email", kind: "out", body: { en: "Maplewood — award approval needed\nReroof the clubhouse · Summit Roofing · $16,400\n4 bids received (range $16,400–$23,900). Scope, all four bids and the site visit notes are attached.\nSign to award: rdl.to/s/2Hq" } },
      ],
      writes: ["award: pending_board_signature", "runner-up: held, not declined"],
      note: "The runner-up goes on hold rather than being turned down, so if the winner's paperwork never lands you move across without re-bidding. The award waits visibly — a manager can't quietly proceed.",
    },
    {
      t: "step 2", title: "Kickoff — the one screen that can't be skipped", by: "manager",
      desc: "Six steps: budget line (required) · the money and the fee · start window · the contract path · who to tell · review.",
      gates: [
        { label: "Charged to 5200 · Repairs & Maintenance — required, pre-picked from the trade default", ok: true },
        { label: "$16,400 agreed · $4,000 deposit (24% of the award, shown as a percentage)", ok: true },
        { label: "Management fee $1,640 — computed, disclosed to the board, its own export line", ok: true },
        { label: "Starts: week of 3 Feb", ok: true },
      ],
      writes: ["work_orders.insert → kind: project · status: draft", "gl_code: 5200 · fee: 1640.00 · deposit: 4000.00"],
      note: "The vendor still hasn't been told anything. And the fee is never shown to them — it's disclosed upward, to the people paying it, and exported as its own line.",
    },
    {
      t: "step 3", title: "The paper — three honest options", by: "system",
      desc: "Redline writes it and both sides e-sign · the vendor sends their own and the community countersigns · no contract at all, under an amount the community sets.",
      gates: [
        { label: "The vendor always signs first", ok: true },
        { label: "Then the community's named signers, in order", ok: true },
        { label: "Merge fields fill themselves: community, vendor, scope, amount, start window, code", ok: true },
        { label: "Vendor contract e-signing is included with Field Services", ok: true },
      ],
      writes: ["sign_requests.insert → vendor, then 2 board signers", "contract.path: generated"],
      note: "Skipping the paper above the community's threshold is allowed — it's just recorded as the manager's decision, with their name on it.",
    },
    {
      t: "step 4", title: "Cleared to start — and not a moment before", by: "system",
      gates: [
        { label: "Contract signed by both sides", ok: true },
        { label: "Certificate of insurance current, meeting community minimums", ok: true },
        { label: "W-9 on file · agreement signed", ok: true },
      ],
      msgs: [
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "You're cleared to start the Maplewood clubhouse roof — week of 3 Feb.\nAccess: gate 4417, skip may go in the north bay, work hours 7am–6pm weekdays.\nPost progress and photos here: rdl.to/w/8Rn" } },
        { to: "board", ch: "email", kind: "out", body: { en: "Summit Roofing is cleared to start — contract signed, insurance verified." } },
      ],
      writes: ["status: pending_paperwork → active", "access details released now, not at award"],
      note: "There's a paperwork window after the award — five business days by default. Miss it and Dana is offered the runner-up she never declined.",
    },
    {
      t: "step 5", title: "Doing it, and proving it", by: "tech",
      desc: "Progress notes and photos from their own page, against the work order. Multi-visit jobs keep one thread.",
      gates: [
        { label: "A change order for $1,200 of rotten decking — approved against the same threshold as the award", ok: true },
        { label: "Counted into the running total, so a small job can't be grown past the board with add-ons", ok: true },
        { label: "Punch list: 3 items added by Dana, ticked off by the crew", ok: true },
      ],
      writes: ["change_orders[0]: 1200.00 approved", "committed: 17,600.00", "punch_list: 3 items · 0 open"],
      note: "Open punch items surface as a flag the moment the final invoice arrives. Nobody has to remember they existed.",
    },
    {
      t: "step 6", title: "Complete — and the board hears it without asking", by: "manager",
      msgs: [
        { to: "board", ch: "email", kind: "out", body: { en: "Maplewood — clubhouse roof complete\nSummit Roofing · agreed $16,400 · 1 change order $1,200 · total committed $17,600\n4 bids taken, awarded to the lowest. 14 progress photos and the signed contract are on the record.\nrdl.to/w/8Rn" } },
      ],
      writes: ["status: complete · completed_at set", "completion is what unlocks a final invoice"],
      note: "This is the email that used to be a conversation at a meeting where nobody had the numbers. If it had been a recurring contract, winning it would have minted a schedule and put the company into the Tuesday loop instead.",
    },
  ]),

  F("invoice", "An invoice, from arrival to paid", "the money · Redline never moves it", "core", [
    {
      t: "step 1", title: "The vendor invoices from their own page", by: "dispatcher",
      desc: "Against a specific work order, not into an inbox. PDF plus the amount they're claiming.",
      gates: [
        { label: "Deposit — allowed before work, no cap, but needs a signed work order", ok: true },
        { label: "Progress — against posted progress", ok: true },
        { label: "Final — needs the work marked complete, or a manager who vouches by name", ok: true },
      ],
      writes: ["vendor_invoices.insert → status: submitted", "gl_code inherited from WO: 5200", "type: final · amount: 13,600.00"],
      note: "No chasing an attachment by email, and no wondering which job it's for. The code is already right because the job carried it.",
    },
    {
      t: "step 1", title: "The PDF is read, and disagreements are surfaced", by: "system",
      gates: [
        { label: "They typed $13,600. The PDF reads $13,600 — they agree", ok: true },
        { label: "If they disagreed we'd keep what they typed and say so, loudly, at review", ok: true },
      ],
      writes: ["parsed_amount: 13600.00 · match"],
      note: "We never silently \"correct\" a vendor's number. We show both and let a human decide, because a typo and an overcharge look identical to software.",
    },
    {
      t: "step 2", title: "The checks run before Dana looks at it", by: "system",
      gates: [
        { label: "Agreed $16,400 + approved change orders $1,200 = committed $17,600", ok: true },
        { label: "Already approved: $4,000 deposit. This invoice: $13,600. Variance: $0", ok: true },
        { label: "Work marked complete · 14 photos · 0 punch items open", ok: true },
        { label: "Going over committed would be flagged red and switch Approve off", ok: true },
      ],
      writes: ["blockers: none", "proof block assembled"],
      note: "The reason an invoice is trustworthy isn't the invoice — it's the record of the work. That's why the proof comes before the decision.",
    },
    {
      t: "step 3", title: "Dana reviews it in five questions", by: "manager",
      desc: "The document · does the maths work · is there proof · what it's charged to · the decision.",
      gates: [
        { label: "Approve is switched off while any rule is broken", ok: true },
        { label: "She can still vouch for it — her name and reason go on the record", ok: true },
        { label: "Sending it back tells the vendor exactly what she typed, verbatim", ok: true },
      ],
      writes: ["needs_board: true — 13,600 ≥ 10,000 threshold"],
      note: "\"Approve anyway, I vouch for it\" exists because software that can't be overridden gets worked around. What matters is that the override is signed.",
    },
    {
      t: "step 4", title: "Over the threshold, so the board signs", by: "board",
      msgs: [
        { to: "board", ch: "email", kind: "out", body: { en: "Maplewood — invoice approval needed\nSummit Roofing · final · $13,600 · clubhouse roof\nAgreed $16,400 + $1,200 change order. $4,000 deposit already paid. Complete with 14 photos.\nSign off: rdl.to/s/5Tv" } },
      ],
      writes: ["status: board_review → approved", "signed_by recorded"],
      note: "Same per-community threshold as awards and change orders. One number, three places it applies — so nobody has to learn a second rule.",
    },
    {
      t: "step 5", title: "Handed to accounting as one coded batch", by: "system",
      desc: "Approved invoices leave together — a CSV and a QuickBooks-shaped file, emailed to the AP address on file.",
      gates: [
        { label: "Every line carries its accounting code", ok: true },
        { label: "The management fee is its own line", ok: true },
        { label: "Redline never moves money. Not once, anywhere in this flow", ok: true },
      ],
      writes: ["invoice_batches.insert → 6 invoices · 47,310.00", "emailed to ap@summitproperties.com"],
      note: "This is the seam where a real QuickBooks Online integration will slot in later. Until then, an export batch and an honest \"mark it paid\" beats pretending we're a payments company.",
    },
    {
      t: "step 6", title: "Paid, and the vendor is told", by: "manager",
      msgs: [
        { to: "dispatcher", ch: "SMS", kind: "out", body: { en: "Maplewood has paid your final invoice for the clubhouse roof — $13,600, cheque 20418. Thanks for the work." } },
      ],
      writes: ["status: paid · paid_ref: 20418", "1099 totals: captured, not yet rendered"],
      note: "\"Did you get my invoice?\" and \"has this been paid?\" are the two phone calls that eat a vendor's afternoon and yours. Both are now a text nobody had to send.",
    },
  ]),

];

export const FLOW_GROUPS = [
  { label: "Read this one first", ids: ["spine"] },
  { label: "The daily loop", ids: ["reactive", "stall", "accepted-silence", "recurring"] },
  { label: "Bigger work, out for bids", ids: ["project-bid", "award-kickoff", "invoice"] },
  { label: "Getting a company on", ids: ["vendor-onboard", "crew-onboard", "solo-vendor", "multi-property", "support"] },
  { label: "When it goes wrong", ids: ["coi"] },
  { label: "Still a proposal — not built", ids: ["voice"] },
];

export const NOT_IN_MVP = [
  "Outbound robocalls to crew — nobody wants those, ever",
  "Inbound crew calls to the concierge — designed, not built (see the last flow)",
  "In-house HOA staff as an assignee — the seam is built, the screen isn't",
  "A real accounting integration — today it's an export batch and \"mark it paid\"",
  "1099 vendor totals — everything needed is captured, nothing renders it yet",
  "A vendor scorecard board — every event is logged, the screen isn't built",
  "Marketplace discovery — finding a company nobody in your network has referred",
  "Licence lookups against state registries · geo-radius matching · bulk vendor import",
];

